import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Search, MapPin, Calendar, Users, Zap, Heart, ArrowRight,
  CheckCircle, ChevronRight, Dumbbell, Trophy, Target,
  UserPlus, Star, Phone, Gamepad2, Timer
} from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function ForPlayers() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo.png" alt="SetPlay" className="h-9 w-9" />
            <span className="font-heading text-xl font-bold uppercase tracking-tight text-court-green">SetPlay</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#why" className="hover:text-court-green transition-colors">Why SetPlay</a>
            <a href="#features" className="hover:text-court-green transition-colors">What You Can Do</a>
            <a href="#made-for" className="hover:text-court-green transition-colors">Made for You</a>
            <button onClick={() => navigate("/")} className="hover:text-court-green transition-colors">Back to Home</button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm font-medium" data-testid="nav-login">Login</Button>
            <Button onClick={() => navigate("/login?tab=register")} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-6 text-sm font-heading uppercase" data-testid="nav-signup">Sign Up as a Player</Button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════ */}
      <section className="pt-16 relative min-h-[92vh] flex items-center" data-testid="players-hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1771909713196-a23e15bdbbf3?w=1920&q=80"
            alt="Basketball players on outdoor court"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/90 via-[#0a1628]/80 to-[#0a1628]/92" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-[#FBBF24] font-heading uppercase tracking-[0.35em] text-sm mb-5">
              For Players
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading uppercase tracking-tighter font-bold text-white text-5xl md:text-7xl lg:text-8xl mb-6 leading-[0.95]" data-testid="hero-heading">
              Book a Court.<br />Play More.<br /><span className="text-[#2563EB]">Stay Active.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-white/75 text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed">
              Sa SetPlay, mas madali nang i-level up ang active lifestyle mo.
            </motion.p>
            <motion.p variants={fadeUp} className="text-white/50 text-base max-w-xl mx-auto mb-10 leading-relaxed">
              Whether gusto mong mag-book ng court, sumali sa game, discover fitness activities, o makahanap ng bagong team mates — nasa iisang platform na lahat.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Button onClick={() => navigate("/login?tab=register")} size="lg"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide shadow-lg" data-testid="cta-signup-player">
                <UserPlus className="w-5 h-5 mr-2" />Sign Up as a Player
              </Button>
              <Button onClick={() => navigate("/search")} variant="outline" size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide" data-testid="cta-book-court">
                <Search className="w-5 h-5 mr-2" />Book a Court Now
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHY SETPLAY FOR PLAYERS?
          ═══════════════════════════════════════════════ */}
      <section id="why" className="py-20 md:py-32 bg-white" data-testid="why-setplay-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
            {/* Left: Problem */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-[#2563EB] font-heading uppercase tracking-[0.2em] text-sm mb-3">
                Less Hassle. More Play.
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl mb-6">
                Why SetPlay<br />for Players?
              </motion.h2>
              <motion.p variants={fadeUp} className="text-slate-700 text-base md:text-lg leading-relaxed">
                Ang SetPlay ay ginawa para sa mga players na gustong gawing mas simple, mas exciting, at mas rewarding ang sports and fitness journey nila.
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-500 text-base mt-4 leading-relaxed">
                Hindi mo na kailangang maghanap sa iba't ibang chats, groups, at contacts para lang makapaglaro o makasali sa activity. With SetPlay, mas mabilis kang makakakilos, makakalaro, at makaka-connect.
              </motion.p>
            </motion.div>

            {/* Right: Key promises */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-6">
              {[
                { icon: Zap, title: "Less hassle", desc: "No more scattered chats, calls, and group messages just to book or join a game." },
                { icon: Gamepad2, title: "More play", desc: "Find courts, games, and activities faster — all in one platform." },
                { icon: Heart, title: "More movement", desc: "Stay consistent with sports and fitness that match your schedule." },
                { icon: Users, title: "More connection", desc: "Meet fellow players, training buddies, and communities who share your energy." },
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-[#2563EB]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#2563EB]/10 transition-colors">
                    <item.icon className="w-6 h-6 text-[#2563EB] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-heading uppercase tracking-tight font-bold text-court-green text-base">{item.title}</p>
                    <p className="text-slate-600 text-sm leading-relaxed mt-1">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHAT YOU CAN DO — FEATURE CARDS
          ═══════════════════════════════════════════════ */}
      <section id="features" className="py-20 md:py-32 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-[#2563EB] font-heading uppercase tracking-[0.2em] text-sm mb-3">
              Lahat sa Iisang Platform
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl">
              What You Can Do with SetPlay
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 auto-rows-auto">
            {/* Card 1: Book Courts (Hero Card) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-8 lg:col-span-8 row-span-2 bg-[#0a1628] text-white rounded-lg border border-white/10 p-8 md:p-12 flex flex-col justify-between hover:-translate-y-1 transition-transform" data-testid="feature-card-book">
              <div>
                <MapPin className="w-8 h-8 text-[#2563EB] mb-5" strokeWidth={1.5} />
                <h3 className="font-heading uppercase tracking-tight font-bold text-2xl md:text-3xl mb-2">Book Courts with Ease</h3>
                <p className="text-white/50 text-sm font-heading uppercase tracking-wider mb-4">Play starts with one easy booking.</p>
                <p className="text-white/70 text-base leading-relaxed mb-6 max-w-lg">
                  Maghanap at mag-book ng courts based on your preferred sport, location, at schedule. Hindi na kailangan ng mahabang usapan — pick, book, play.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-[#2563EB] font-heading">Benefits:</p>
                {["Fast and convenient booking", "Easier planning with friends or team", "Better access to available venues and time slots"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
                    <span className="text-white/65 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 2: Join Games (Tall) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 row-span-2 bg-white rounded-lg border border-[#2563EB]/20 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-join">
              <Calendar className="w-8 h-8 text-[#2563EB] mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Join Games Anytime</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-4">Your next game could be one tap away.</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Kulang ang players? Wala pang full team? No problem. SetPlay helps you discover and join games near you.
              </p>
              <div className="space-y-2.5">
                {["More chances to play", "Great for solo players or partial teams", "Less waiting, more action"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 3: Fitness Activities */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-fitness">
              <Dumbbell className="w-8 h-8 text-court-orange mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Stay Active with Fitness Activities</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">Staying active should feel easy and exciting.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Hindi lang pang-court sports ang SetPlay. You can also explore fitness and wellness activities that fit your lifestyle.
              </p>
            </motion.div>

            {/* Card 4: Meet Team Mates (Tall, colored) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 row-span-2 bg-[#2563EB] text-white rounded-lg border border-[#2563EB] p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-connect">
              <Users className="w-8 h-8 text-[#FBBF24] mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-xl mb-2">Meet Team Mates and New Friends</h3>
              <p className="text-white/45 text-sm font-heading uppercase tracking-wider mb-4">From kalaro to community.</p>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                Sports is better when shared with the right people. SetPlay helps you connect with players, training buddies, and communities who share your energy.
              </p>
              <div className="space-y-2.5">
                {["Expand your sports circle", "Meet future team mates", "Build real connections through play"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <ChevronRight className="w-4 h-4 text-[#FBBF24] flex-shrink-0" />
                    <span className="text-white/65 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 5: Improve Skills */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-improve">
              <Target className="w-8 h-8 text-court-green mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Improve Your Skills and Health</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">More play means more progress.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Every game, training session, and activity is a chance to grow. Sharpen your skills, build confidence, and improve your overall well-being.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          MADE FOR REAL PLAYERS
          ═══════════════════════════════════════════════ */}
      <section id="made-for" className="py-20 md:py-32 bg-white" data-testid="made-for-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-[#2563EB] font-heading uppercase tracking-[0.2em] text-sm mb-3">
              Para sa Lahat ng Level
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl mb-4">
              Made for Real Players
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              SetPlay helps you find your game, your rhythm, and your people.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {[
              { icon: Gamepad2, title: "Casual Player", desc: "Just want to play when you can", color: "bg-blue-50 text-blue-600" },
              { icon: Calendar, title: "Weekend Warrior", desc: "Your weekends are for the court", color: "bg-amber-50 text-amber-600" },
              { icon: Dumbbell, title: "Fitness Enthusiast", desc: "Health and movement are your priority", color: "bg-green-50 text-green-600" },
              { icon: Trophy, title: "Competitive Athlete", desc: "Always training, always improving", color: "bg-red-50 text-red-600" },
              { icon: Star, title: "Beginner", desc: "Starting your journey one game at a time", color: "bg-purple-50 text-purple-600" },
            ].map((type, i) => (
              <motion.div key={i} variants={fadeUp}
                className="text-center p-6 rounded-lg border border-slate-200 hover:-translate-y-1 transition-transform group" data-testid={`player-type-${i}`}>
                <div className={`w-14 h-14 rounded-xl ${type.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <type.icon className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-sm mb-1">{type.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{type.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          MORE THAN JUST A PLATFORM
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-32 bg-[#0a1628]" data-testid="more-than-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-[#FBBF24] font-heading uppercase tracking-[0.2em] text-sm mb-4">
              More Than Just a Platform
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-white text-3xl md:text-5xl mb-6 leading-tight">
              SetPlay is where players go to move better, play more often, and live more actively.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/55 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Because every booking is not just a schedule — it's a chance to have fun, stay healthy, and be part of something bigger.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CTA — READY TO GET IN THE GAME?
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white" data-testid="cta-block">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="bg-[#2563EB] text-white rounded-[32px] p-10 md:p-16 text-center shadow-2xl">
            <h2 className="font-heading uppercase tracking-tight font-bold text-3xl md:text-4xl mb-4">
              Ready to Get in the Game?
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Join SetPlay today and discover a better way to book courts, join games, stay active, and connect with fellow players.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Button onClick={() => navigate("/login?tab=register")} size="lg"
                className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-bottom-signup">
                <UserPlus className="w-5 h-5 mr-2" />Sign Up as a Player
              </Button>
              <Button onClick={() => navigate("/search")} variant="outline" size="lg"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-bottom-book">
                <Search className="w-5 h-5 mr-2" />Book Your First Court
              </Button>
              <Button onClick={() => navigate("/search")} variant="outline" size="lg"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-bottom-explore">
                <Dumbbell className="w-5 h-5 mr-2" />Explore Activities
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════ */}
      <footer className="py-12 bg-[#0a2a1e] text-white/60" data-testid="footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="SetPlay" className="h-8 w-8" />
                <span className="font-heading text-lg font-bold text-white uppercase">SetPlay</span>
              </div>
              <p className="text-sm leading-relaxed">Connecting Filipino communities through sports. One game at a time.</p>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Platform</h4>
              <div className="space-y-2 text-sm">
                <a href="/#how-it-works" className="block hover:text-white transition-colors">How It Works</a>
                <a href="/search" className="block hover:text-white transition-colors">Find Courts</a>
                <a href="/sessions" className="block hover:text-white transition-colors">Browse Sessions</a>
                <a href="/events" className="block hover:text-white transition-colors">Events</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Join Us</h4>
              <div className="space-y-2 text-sm">
                <a href="/for-players" className="block hover:text-white transition-colors font-medium text-[#2563EB]">For Players</a>
                <a href="/for-venues" className="block hover:text-white transition-colors">For Venues</a>
                <a href="/login?tab=register" className="block hover:text-white transition-colors">For Coaches</a>
                <a href="/sp/search" className="block hover:text-white transition-colors">For Sports Professionals</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Contact</h4>
              <div className="space-y-2 text-sm">
                <p>gamena@setplay.net</p>
                <p>Metro Manila, Philippines</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm">Proudly Built for the Philippines by <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FBBF24] underline underline-offset-2 transition-colors">SusTek Labs</a></p>
            <div className="flex gap-4 text-sm">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
